import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Package, Edit3, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function POSCatalogue() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "management", "general_manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/pos-materials").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/product-categories").then(r => setCategories(r.data));
    api.get("/products").then(r => setModels(r.data));
  }, []);

  const del = async (id) => {
    if (!window.confirm("Archive this POS material?")) return;
    await api.delete(`/pos-materials/${id}`); toast.success("Archived"); load();
  };

  const lowStock = items.filter(i => i.active && (i.warehouse_stock || 0) < (i.reorder_level || 0));

  return (
    <div className="space-y-6" data-testid="pos-catalogue-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-2"><Package className="w-8 h-8 text-[#003DA5]" /> POS Materials Catalogue</h1>
          <p className="text-slate-600 mt-1">Configure POS items with warehouse stock, reorder levels and per-unit quantities</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="mlily-btn h-11 px-5 rounded-lg font-semibold text-sm flex items-center gap-2" data-testid="new-pos-btn">
            <Plus className="w-4 h-4" /> New POS Material
          </button>
        )}
      </div>

      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="low-stock-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900">{lowStock.length} material{lowStock.length !== 1 ? "s" : ""} below reorder level</div>
            <div className="text-amber-700 mt-0.5">{lowStock.map(m => `${m.name} (${m.warehouse_stock}/${m.reorder_level})`).join(", ")}</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Material</th>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Applies to</th>
              <th className="text-left px-4 py-3">Qty/unit</th>
              <th className="text-left px-4 py-3">Warehouse</th>
              <th className="text-left px-4 py-3">Status</th>
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {items.map(m => {
              const cat = categories.find(c => c.id === m.category_id);
              const model = models.find(md => md.id === m.model_id);
              const scope = [cat?.name, model?.name, m.size].filter(Boolean).join(" / ") || "All products";
              const lo = (m.warehouse_stock || 0) < (m.reorder_level || 0);
              return (
                <tr key={m.id} className="border-t hover:bg-slate-50" data-testid={`pos-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.name}{m.mandatory && <span className="ml-2 text-[10px] text-red-600 font-bold">MANDATORY</span>}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">{m.code || "—"}</td>
                  <td className="px-4 py-3 text-xs">{scope}</td>
                  <td className="px-4 py-3">{m.quantity_per_unit}</td>
                  <td className="px-4 py-3"><span className={`font-semibold ${lo ? "text-red-600" : "text-slate-700"}`}>{m.warehouse_stock}</span> <span className="text-xs text-slate-500">/ {m.reorder_level}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${m.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.active ? "Active" : "Archived"}</span></td>
                  {canEdit && <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(m); setShowForm(true); }} className="p-1.5 text-slate-500 hover:text-[#003DA5]" data-testid={`edit-pos-${m.id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(m.id)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`delete-pos-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>}
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={canEdit ? 7 : 6} className="px-4 py-12 text-center text-slate-500">No POS materials yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && <POSForm initial={editing} categories={categories} models={models} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function POSForm({ initial, categories, models, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    name: "", code: "", category_id: "", model_id: "", size: "",
    quantity_per_unit: 1, mandatory: false, warehouse_stock: 0, reorder_level: 10, active: true, description: "",
  });
  const filteredModels = f.category_id ? models.filter(m => m.category_id === f.category_id) : models;
  const sizeOptions = f.model_id ? (models.find(m => m.id === f.model_id)?.available_sizes || []) : [];
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f };
    body.category_id = body.category_id || null;
    body.model_id = body.model_id || null;
    body.size = body.size || null;
    body.quantity_per_unit = Number(body.quantity_per_unit);
    body.warehouse_stock = Number(body.warehouse_stock);
    body.reorder_level = Number(body.reorder_level);
    try {
      if (initial?.id) await api.patch(`/pos-materials/${initial.id}`, body);
      else await api.post("/pos-materials", body);
      toast.success(initial ? "Updated" : "Created"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">{initial ? "Edit POS Material" : "New POS Material"}</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <F label="Name *"><input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-name" /></F>
          <F label="POS code"><input value={f.code || ""} onChange={e => setF({ ...f, code: e.target.value })} className="w-full h-11 px-3 border rounded-lg font-mono" data-testid="pos-code" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Applies to category"><select value={f.category_id || ""} onChange={e => setF({ ...f, category_id: e.target.value, model_id: "", size: "" })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-category">
              <option value="">Any</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></F>
            <F label="Applies to model"><select value={f.model_id || ""} onChange={e => setF({ ...f, model_id: e.target.value, size: "" })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-model" disabled={!f.category_id}>
              <option value="">Any</option>{filteredModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select></F>
            <F label="Applies to size"><select value={f.size || ""} onChange={e => setF({ ...f, size: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-size" disabled={!sizeOptions.length}>
              <option value="">Any</option>{sizeOptions.map(s => <option key={s}>{s}</option>)}
            </select></F>
            <F label="Qty per floor unit *"><input required type="number" min="0" value={f.quantity_per_unit} onChange={e => setF({ ...f, quantity_per_unit: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-qty-per-unit" /></F>
            <F label="Warehouse stock"><input type="number" min="0" value={f.warehouse_stock} onChange={e => setF({ ...f, warehouse_stock: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-stock" /></F>
            <F label="Reorder level"><input type="number" min="0" value={f.reorder_level} onChange={e => setF({ ...f, reorder_level: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="pos-reorder" /></F>
          </div>
          <F label="Description"><textarea value={f.description || ""} onChange={e => setF({ ...f, description: e.target.value })} rows={2} className="w-full p-3 border rounded-lg" /></F>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.mandatory} onChange={e => setF({ ...f, mandatory: e.target.checked })} data-testid="pos-mandatory" /> Mandatory</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.active} onChange={e => setF({ ...f, active: e.target.checked })} /> Active</label>
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-pos-btn">Save</button></div>
      </form>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
